import {
  ALL,
  DEFAULT_CURRENCY,
  categoryChips,
  outfitMeta,
  outfitPieces,
  outfitStatus,
  visibleItems,
  type CategoryFilter,
  type Outfit,
  type User,
  type View,
} from '@closetly/core';
import { useMemo, useState } from 'react';
import { useItemEditor } from '../lib/editor';
import { newRecordId } from '../lib/ids';
import { storedImageUrl } from '../lib/image';
import {
  useBoards,
  useDeleteOutfit,
  useItems,
  useMe,
  useOutfits,
  useSaveBoard,
  useSaveOutfit,
  willQueue,
} from '../lib/queries';
import { navigate } from '../lib/router';
import { useToast } from '../lib/toast';
import { Collage } from './Collage';

/** Create or change an outfit: pick pieces, see the collage grow, tag it. */
export function OutfitEditor({ user, id }: { user: User; id: string | null }) {
  const outfits = useOutfits(user.id);
  const existing = id ? outfits.data?.find((o) => o.id === id) : undefined;

  if (id && !existing) {
    return (
      <div className="empty">
        <strong>{outfits.isPending ? 'Lade…' : 'Outfit nicht gefunden'}</strong>
        <button type="button" className="btn" onClick={() => navigate('/outfits')}>
          Zu den Outfits
        </button>
      </div>
    );
  }
  // keyed so the form starts from the outfit as it is when opened
  return <EditorForm key={id ?? 'new'} user={user} outfit={existing ?? null} />;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function EditorForm({ user, outfit }: { user: User; outfit: Outfit | null }) {
  const items = useItems(user.id);
  const me = useMe(user);
  const saveOutfit = useSaveOutfit(user.id);
  const deleteOutfit = useDeleteOutfit(user.id);
  const boards = useBoards(user.id);
  const saveBoard = useSaveBoard(user.id);
  const allBoards = useMemo(() => boards.data ?? [], [boards.data]);
  const editor = useItemEditor();
  const toast = useToast();
  const currency = me.data?.currency || DEFAULT_CURRENCY;

  const [name, setName] = useState(outfit?.name ?? '');
  const [picked, setPicked] = useState<string[]>(outfit?.items ?? []);
  const [onBoards, setOnBoards] = useState<string[]>(() =>
    outfit ? allBoards.filter((b) => b.outfits.includes(outfit.id)).map((b) => b.id) : [],
  );
  const [note, setNote] = useState(outfit?.note ?? '');
  const [view, setView] = useState<View>('open');
  const [cat, setCat] = useState<CategoryFilter>(ALL);

  const all = useMemo(() => items.data ?? [], [items.data]);
  const pieces = outfitPieces({ items: picked }, all);
  const status = outfitStatus(pieces);
  const chips = useMemo(() => categoryChips(all, view), [all, view]);
  const activeCat = chips.some((c) => c.cat === cat) ? cat : ALL;
  const choices = useMemo(() => visibleItems(all, { view, cat: activeCat, sort: 'new' }), [all, view, activeCat]);

  const back = () => navigate(outfit ? `/outfits/${outfit.id}` : '/outfits');
  const failed = () => toast('Speichern fehlgeschlagen. Versuch es nochmal.');

  function save() {
    const data = { name: name.trim(), items: picked, note: note.trim() };
    const id = outfit?.id ?? newRecordId();
    if (outfit) saveOutfit.mutate({ id, owner: user.id, data }, { onError: failed });
    else saveOutfit.mutate({ createId: id, owner: user.id, data }, { onError: failed });
    // boards change after the outfit exists (all writes run in order)
    for (const b of allBoards) {
      const was = b.outfits.includes(id);
      const now = onBoards.includes(b.id);
      if (was !== now)
        saveBoard.mutate(
          { id: b.id, owner: user.id, ...(now ? { add: { outfits: [id] } } : { remove: { outfits: [id] } }) },
          { onError: failed },
        );
    }
    navigate(`/outfits/${id}`, { replace: true });
    toast(willQueue() ? 'Gespeichert. Wird gesendet, sobald du wieder online bist.' : 'Outfit gespeichert');
  }

  function remove() {
    if (!outfit || !confirm(`„${outfit.name || 'Outfit'}“ löschen? Die Teile bleiben auf deiner Liste.`)) return;
    deleteOutfit.mutate({ id: outfit.id, owner: user.id }, { onError: () => toast('Löschen fehlgeschlagen.') });
    navigate('/outfits', { replace: true });
    toast('Outfit gelöscht');
  }

  return (
    <>
      <button type="button" className="back linkbtn" onClick={back}>
        ‹ Abbrechen
      </button>
      <div className="detail editor">
        <div className="detail-visual sticky">
          <Collage pieces={pieces} big />
          <p className="hint">{outfitMeta(status, currency)}</p>
        </div>

        <form
          className="detail-info"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <h2>{outfit ? 'Outfit bearbeiten' : 'Neues Outfit'}</h2>
          <div className="field">
            <label htmlFor="o-name">Name</label>
            <input
              id="o-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Herbst Casual"
              maxLength={80}
              autoComplete="off"
            />
          </div>

          <div className="field">
            <span className="label">Teile</span>
            <div className="picker-head">
              <div className="seg" role="group" aria-label="Teile aus">
                {(
                  [
                    ['open', 'Wunschliste'],
                    ['bought', 'Gekauft'],
                  ] as const
                ).map(([v, label]) => (
                  <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
                    {label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn"
                onClick={() => editor.openNew({ onCreated: (newId) => setPicked((p) => [...p, newId]) })}
              >
                + Neues Teil per Link
              </button>
            </div>
            {chips.length > 2 && (
              <div className="chips" role="group" aria-label="Kategorie">
                {chips.map((c) => (
                  <button
                    key={c.cat}
                    type="button"
                    className="chip"
                    aria-pressed={c.cat === activeCat}
                    onClick={() => setCat(c.cat)}
                  >
                    {c.cat}
                  </button>
                ))}
              </div>
            )}
            {choices.length === 0 ? (
              <p className="hint">
                {view === 'open'
                  ? 'Keine offenen Wunschteile. Füg eins per Link hinzu.'
                  : 'Noch nichts gekauft.'}
              </p>
            ) : (
              <div className="picker">
                {choices.map((it) => {
                  const on = picked.includes(it.id);
                  const img = storedImageUrl(it);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      className={`pick${on ? ' on' : ''}`}
                      aria-pressed={on}
                      aria-label={it.name}
                      onClick={() => setPicked((p) => toggle(p, it.id))}
                    >
                      {img ? <img src={img} alt="" loading="lazy" /> : <span className="pick-text">{it.name}</span>}
                      <span className="pick-name">{it.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {allBoards.length > 0 && (
            <div className="field">
              <span className="label">Auf Boards</span>
              <div className="chips wrap-chips" role="group" aria-label="Boards">
                {allBoards.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="chip"
                    aria-pressed={onBoards.includes(b.id)}
                    onClick={() => setOnBoards((l) => toggle(l, b.id))}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="field">
            <label htmlFor="o-note">Notiz</label>
            <textarea
              id="o-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Für die Hochzeit im Mai, Schuhe in Braun…"
              maxLength={1000}
            />
          </div>

          <div className="sheet-actions">
            {outfit && (
              <button type="button" className="btn danger" onClick={remove}>
                Outfit löschen
              </button>
            )}
            <div className="right">
              <button type="button" className="btn" onClick={back}>
                Abbrechen
              </button>
              <button type="submit" className="btn ink">
                Speichern
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
